import { useId, useState } from 'react'
import type { Navigate } from '../App'
import { useStore } from '../state/store'
import { languageOf, type LanguageDef } from '../lib/languages'
import { levelFor, scaleStep } from '../lib/levels'
import { SpeakButton } from '../components/SpeakButton'
import {
  grammarFor,
  parseEmphasis,
  parsePieces,
  plainText,
  roleColor,
  slotIndex,
  slotLabel,
  splitGrammar,
  TOPIC_LABEL,
  type GrammarExample,
  type GrammarRule,
  type Piece,
} from '../data/grammar'

/**
 * The rules behind the islands.
 *
 * A reference you dip into, like the Refresher: nothing here is drilled,
 * scored or scheduled. It follows the level control the way the islands do —
 * the rules at or below your level are listed, and the harder ones wait behind
 * a button with the count on it — because a beginner who opens a grammar page
 * and meets 与其…不如 first closes it.
 *
 * Each rule shows its formula and its first example straight away. The formula
 * is the rule; the example, split into the pieces the formula names, is what
 * makes it land. The remaining examples, the common mistake and the note are
 * one press further, so the page stays scannable.
 */
export function GrammarView({ navigate }: { navigate: Navigate }) {
  const { data } = useStore()
  const lang = languageOf(data.settings.activeLanguage)
  const level = levelFor(data.settings, lang.code)
  const rules = grammarFor(lang.code)
  const { ready, ahead } = splitGrammar(rules, level)

  // Collapsed on every visit, like the islands above your level.
  const [showAhead, setShowAhead] = useState(false)
  const [expandAll, setExpandAll] = useState(false)

  return (
    <>
      <header className="page-head">
        <div className="eyebrow">Grammar</div>
        <h1 className="page-title">
          The rules behind the islands{' '}
          <span className={lang.code === 'zh' ? 'page-title-zh' : ''} lang={lang.speech}>
            · {lang.ui.grammar}
          </span>
        </h1>
        <p className="lede">
          A few rules make a great many sentences make sense. Each one has a formula, and its
          examples are split into the pieces the formula names — so you can see which part is
          the time and which is the place. Nothing here is drilled; when a rule clicks, go and
          find it in your islands.
        </p>
      </header>

      {rules.length === 0 ? (
        <div className="empty">There is no grammar for {lang.name} yet.</div>
      ) : (
        <>
          <div className="row" style={{ marginBottom: '1rem', gap: '0.6rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn is-small is-quiet"
              aria-pressed={expandAll}
              onClick={() => setExpandAll((v) => !v)}
            >
              {expandAll ? 'Show one example each' : 'Show every example'}
            </button>
          </div>

          {ready.length > 0 ? (
            <RuleGroups rules={ready} lang={lang} expandAll={expandAll} />
          ) : (
            <div className="empty">
              Nothing at {scaleStep(lang.code, level)} — every rule here is pitched higher. Move
              the level in the rail, or open the harder ones below.
            </div>
          )}

          {ahead.length > 0 && (
            <>
              <div className="divider" style={{ marginTop: '1.6rem' }} />
              <button
                type="button"
                className="btn is-small is-quiet"
                aria-expanded={showAhead}
                onClick={() => setShowAhead((v) => !v)}
                style={{ marginTop: '1.1rem' }}
              >
                {showAhead ? 'Hide' : 'Show'} {ahead.length} {ahead.length === 1 ? 'rule' : 'rules'}{' '}
                above {scaleStep(lang.code, level)}
              </button>
              {showAhead && (
                <>
                  <p className="muted" style={{ fontSize: '0.86rem', margin: '0.9rem 0 1rem' }}>
                    Pitched past where you said you are. Read ahead freely, or move your level in the
                    rail and they join the list above.
                  </p>
                  <RuleGroups rules={ahead} lang={lang} expandAll={expandAll} />
                </>
              )}
            </>
          )}
        </>
      )}

      <button
        type="button"
        className="btn is-quiet is-small"
        onClick={() => navigate({ view: 'islands' })}
        style={{ marginTop: '1.4rem' }}
      >
        ← Islands
      </button>
    </>
  )
}

/** Rules under a heading per level: HSK 1, HSK 2 — or A1, A2. */
function RuleGroups({
  rules,
  lang,
  expandAll,
}: {
  rules: GrammarRule[]
  lang: LanguageDef
  expandAll: boolean
}) {
  const levels = [...new Set(rules.map((r) => r.level))]
  return (
    <>
      {levels.map((n) => (
        <section key={n} className="gram-level" aria-label={scaleStep(lang.code, n)}>
          <h2 className="gram-level-head">{scaleStep(lang.code, n)}</h2>
          {rules
            .filter((r) => r.level === n)
            .map((rule) => (
              <RuleCard key={rule.id} rule={rule} lang={lang} expandAll={expandAll} />
            ))}
        </section>
      ))}
    </>
  )
}

function RuleCard({ rule, lang, expandAll }: { rule: GrammarRule; lang: LanguageDef; expandAll: boolean }) {
  const [open, setOpen] = useState(false)
  const moreId = useId()
  const shown = expandAll || open
  const [first, ...rest] = rule.examples

  const extras = [
    rest.length > 0 && `${rest.length} more ${rest.length === 1 ? 'example' : 'examples'}`,
    rule.pitfall && 'a common mistake',
    rule.note && 'a note',
  ].filter(Boolean) as string[]
  const extrasText =
    extras.length > 1 ? `${extras.slice(0, -1).join(', ')} and ${extras[extras.length - 1]}` : extras[0]

  return (
    <article className="card gram-rule" aria-labelledby={`${moreId}-title`}>
      <div className="gram-rule-top">
        <span className="eyebrow" style={{ margin: 0 }}>
          {TOPIC_LABEL[rule.topic]}
        </span>
        <span className="badge">{scaleStep(lang.code, rule.level)}</span>
      </div>

      <h3 id={`${moreId}-title`} className="gram-rule-title">
        {rule.title}
      </h3>
      <p className="gram-rule-text">{prose(rule.rule)}</p>

      <Formula rule={rule} lang={lang} />

      <Example rule={rule} example={first} lang={lang} />

      {shown && (
        <div id={moreId} className="gram-more">
          {rest.map((ex, n) => (
            <Example key={n} rule={rule} example={ex} lang={lang} />
          ))}

          {rule.pitfall && (
            <div className="gram-pitfall">
              <div className="gram-pitfall-line is-wrong">
                <span className="gram-mark" aria-label="Wrong">
                  ✗
                </span>
                <div>
                  <div className={lang.code === 'zh' ? 'hanzi gram-said' : 'gram-said'} lang={lang.speech}>
                    <Emphasis text={rule.pitfall.wrong} />
                  </div>
                  {rule.pitfall.wrongPinyin && <div className="pinyin">{rule.pitfall.wrongPinyin}</div>}
                </div>
              </div>
              <div className="gram-pitfall-line is-right">
                <span className="gram-mark" aria-label="Right">
                  ✓
                </span>
                <div>
                  <div className={lang.code === 'zh' ? 'hanzi gram-said' : 'gram-said'} lang={lang.speech}>
                    <Emphasis text={rule.pitfall.right} />
                  </div>
                  {rule.pitfall.rightPinyin && <div className="pinyin">{rule.pitfall.rightPinyin}</div>}
                </div>
                {/* Only the correct version is playable: hearing the mistake on
                    repeat is exactly how it gets learned. */}
                <SpeakButton text={plainText(rule.pitfall.right)} language={lang.code} withSlow />
              </div>
              <p className="english gram-why">{prose(rule.pitfall.why)}</p>
            </div>
          )}

          {rule.note && <p className="muted gram-note">{prose(rule.note)}</p>}
        </div>
      )}

      {/* After the extra content, not between the examples: open, the card
          reads top to bottom and the way to close it is where you finish. */}
      {extras.length > 0 && !expandAll && (
        <button
          type="button"
          className="btn is-small is-quiet"
          aria-expanded={open}
          aria-controls={moreId}
          onClick={() => setOpen((v) => !v)}
          style={{ marginTop: '0.6rem' }}
        >
          {open ? 'Show less' : `Show ${extrasText}`}
        </button>
      )}
    </article>
  )
}

/**
 * The formula as a row of chips.
 *
 * Roles wear the color their pieces wear in the examples below; literal words
 * are set in the language's own type, bold, because they ARE the grammar. A
 * sequence is joined by +; a contrast is a set of alternatives, so it isn't.
 */
function Formula({ rule, lang }: { rule: GrammarRule; lang: LanguageDef }) {
  const contrast = rule.shape === 'contrast'
  return (
    <p className="gram-formula" aria-label={`Formula: ${rule.pattern.map(slotLabel).join(contrast ? ', or ' : ' plus ')}`}>
      {rule.pattern.map((slot, n) => {
        const color = roleColor(rule, n)
        return (
          <span key={n} className="gram-formula-item" aria-hidden="true">
            {n > 0 && <span className="gram-plus">{contrast ? '·' : '+'}</span>}
            {color === null ? (
              <span
                className={`gram-chip is-literal ${lang.code === 'zh' ? 'hanzi' : ''}`}
                lang={lang.speech}
              >
                {slotLabel(slot)}
              </span>
            ) : (
              <span className={`gram-chip slot-${color}`}>{slotLabel(slot)}</span>
            )}
          </span>
        )
      })}
    </p>
  )
}

/** One example: the labeled sentence, pinyin, English, and audio. */
function Example({ rule, example, lang }: { rule: GrammarRule; example: GrammarExample; lang: LanguageDef }) {
  return (
    <div className="gram-example">
      <div style={{ flex: 1, minWidth: 0 }}>
        <Sentence rule={rule} source={example.text} lang={lang} />
        {example.pinyin && <div className="pinyin gram-pinyin">{example.pinyin}</div>}
        <div className="english gram-english">{example.english}</div>
      </div>
      <SpeakButton text={plainText(example.text)} language={lang.code} withSlow />
    </div>
  )
}

/**
 * A sentence with each tagged piece labeled beneath.
 *
 * Mandarin has no spaces, so the pieces are laid out as a wrapping row and a
 * long sentence breaks between pieces. The Latin languages flow as ordinary
 * text with the labeled pieces sitting inline, so the spaces in the sentence
 * stay exactly where they were written.
 */
function Sentence({ rule, source, lang }: { rule: GrammarRule; source: string; lang: LanguageDef }) {
  const zh = lang.code === 'zh'
  return (
    <div className={`gram-sentence ${zh ? 'hanzi is-rows' : ''}`} lang={lang.speech}>
      {withPunctuation(parsePieces(source)).map(({ piece, lead, trail }, n) => {
        if (piece.slot === null) {
          return (
            <span key={n} className="gram-piece">
              <Emphasis text={piece.text} />
            </span>
          )
        }
        const index = slotIndex(rule, piece.slot)
        const color = roleColor(rule, index)
        if (color === null) {
          return (
            <span key={n} className="gram-piece">
              {lead}
              <span className="is-literal">
                <Emphasis text={piece.text} />
              </span>
              {trail}
            </span>
          )
        }
        return (
          <span key={n} className={`gram-piece is-tagged slot-${color}`}>
            <span className="gram-piece-line">
              {lead && <span className="gram-piece-punct">{lead}</span>}
              <span className="gram-piece-text">
                <Emphasis text={piece.text} />
              </span>
              {trail && <span className="gram-piece-punct">{trail}</span>}
            </span>
            <span className="gram-piece-label" lang="en">
              {piece.slot}
            </span>
          </span>
        )
      })}
    </div>
  )
}

/**
 * Punctuation touching a word at the start or end of a plain run — with a
 * French non-breaking space beside it, which is that space’s whole job.
 * Deliberately no ordinary space: punctuation after a space isn’t touching.
 */
const PUNCT = '[。，、？！；：.,?!;:¿¡«»“”"…)(]+'
const LEADING_PUNCT = new RegExp(`^\\u00a0?${PUNCT}`, 'u')
const TRAILING_PUNCT = new RegExp(`${PUNCT}\\u00a0?$`, 'u')

interface Placed {
  piece: Piece
  lead: string
  trail: string
}

/**
 * Carry punctuation into the labeled piece it touches.
 *
 * A labeled piece is as wide as its label, so a short word under a long label
 * — 忙 over ADJECTIVE, du over SUBJECT — leaves a gap before whatever follows,
 * and a comma or full stop on the far side of that gap reads as a stray mark.
 * Carried inside the piece, next to the word but outside its underline, it
 * stays where it was written. ¿ and ¡ in front are carried the same way.
 *
 * Only the touching part moves: in "[regnet](Verb), bleiben wir…" the comma
 * goes with regnet and " bleiben wir…" keeps the rest.
 */
function withPunctuation(pieces: Piece[]): Placed[] {
  const out: Placed[] = []
  let pendingLead = ''

  pieces.forEach((original, i) => {
    let piece = original
    const prev = out[out.length - 1]
    const next = pieces[i + 1]

    if (piece.slot === null && prev && prev.piece.slot !== null) {
      const taken = piece.text.match(LEADING_PUNCT)?.[0]
      if (taken) {
        prev.trail += taken
        piece = { ...piece, text: piece.text.slice(taken.length) }
      }
    }
    if (piece.slot === null && next && next.slot !== null) {
      const taken = piece.text.match(TRAILING_PUNCT)?.[0]
      if (taken) {
        piece = { ...piece, text: piece.text.slice(0, -taken.length) }
        pendingLead = taken
      }
    }

    if (piece.slot === null) {
      if (piece.text !== '') out.push({ piece, lead: '', trail: '' })
      return
    }
    out.push({ piece, lead: pendingLead, trail: '' })
    pendingLead = ''
  })

  return out
}

/**
 * Prose with suffixes kept whole.
 *
 * Rule text talks about endings — “-er, -es”, “-ando, -iendo” — and a browser
 * will happily end a line on the hyphen and start the next with “er”, which
 * reads as a stray dash and a stray syllable. A hyphen that begins a suffix is
 * swapped for a non-breaking one on screen; the data stays plain text.
 */
function prose(text: string): string {
  return text.replace(/(^|[\s(“])-(?=\p{L})/gu, `$1${String.fromCharCode(0x2011)}`)
}

function Emphasis({ text }: { text: string }) {
  return (
    <>
      {parseEmphasis(text).map((run, n) =>
        run.strong ? (
          <strong key={n} className="gram-em">
            {run.text}
          </strong>
        ) : (
          <span key={n}>{run.text}</span>
        ),
      )}
    </>
  )
}
